import { ArrowUpRight } from '@phosphor-icons/react';
import { usePublicProfileData } from '@/context/PublicProfileContext';
import { parseDateStr } from '@/utils/dateStr';
import type { ApiRelease } from '@/types/dashboard';

function formatReleaseParts(
  s: string | null,
): { mon: string; year: string } | null {
  const dt = parseDateStr(s);
  if (!dt) return null;
  return {
    mon: dt.toLocaleDateString('en-US', { month: 'short' }),
    year: String(dt.getFullYear()),
  };
}

function Cover({ r, className }: { r: ApiRelease; className: string }) {
  if (r.coverImage) {
    return <img className={className} src={r.coverImage} alt={r.title} />;
  }
  return (
    <span
      className={className}
      style={{ background: r.coverColor ?? 'var(--ap-matte)' }}
      aria-hidden="true"
    />
  );
}

export function Highlights() {
  const { releases } = usePublicProfileData();
  if (releases.length === 0) return null;

  const sorted = [...releases].sort((a, b) => {
    if (a.badge === 'upcoming' && b.badge !== 'upcoming') return -1;
    if (b.badge === 'upcoming' && a.badge !== 'upcoming') return 1;
    return 0;
  });

  return (
    <>
      <div className="ap-divider" />
      <section id="releases">
        <h2 className="ap-section__title">Highlights</h2>
        <div className="ap-hl-feed">
          {sorted.map((r) => {
            const when = formatReleaseParts(r.releaseDate);
            return (
              <div key={r.id} className="ap-hl-row">
                {!when && <Cover r={r} className="ap-hl-row__chip" />}
                {when && (
                  <span className="ap-hl-row__when">
                    {when.mon}
                    <br />
                    {when.year}
                  </span>
                )}
                <span className="ap-hl-row__body">
                  {r.badge && (
                    <span className={`ap-hl-tag ap-hl-tag--${r.badge}`}>
                      {r.badge === 'out' ? 'Out now' : 'Upcoming'}
                    </span>
                  )}
                  <span className="ap-hl-row__title">{r.title}</span>
                  {r.description && (
                    <span className="ap-hl-row__desc">{r.description}</span>
                  )}
                  <span className="ap-hl-row__type">{r.type}</span>
                </span>
                {when && <Cover r={r} className="ap-hl-row__chip" />}
                {r.link && (
                  <a
                    href={r.link}
                    className="ap-visit"
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Visit ${r.title}`}
                  >
                    <ArrowUpRight size={14} weight="bold" />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
